import { useRoute, useRouter } from 'vue-router'
import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { compressToEncodedURIComponent } from 'lz-string'

import { useUri } from '@/stores/uri'
import { useMaCore } from '@/composables/maCore'
import { maLogger } from '@/base/logger'
import { TAXONOMY_TYPES } from '@/base/constants/Taxonomy'
import { Query } from '@/base/constants/route'

export const useTaxonomy = defineStore('taxomony', () => {
  const { maRequests, maNotification } = useMaCore()
  const uriStore = useUri()
  const route = useRoute()
  const router = useRouter()

  const appTaxonomies = ref([])
  const gameTaxonomies = ref([])
  const loading = ref(true)
  const overlayVisibility = ref(false)

  const filterType = computed(
    () => uriStore.appTaxonomies.length
      ? TAXONOMY_TYPES.APP
      : uriStore.gameTaxonomies.length
        ? TAXONOMY_TYPES.GAME
        : TAXONOMY_TYPES.CATEGORY,
  )

  const uriTaxonomyIds = computed(() => [...uriStore.appTaxonomies, ...uriStore.gameTaxonomies])
  const allTaxonomies = computed(() => [...gameTaxonomies.value, ...appTaxonomies.value])

  const flatTaxonomies = computed(
    () => allTaxonomies.value
      .flatMap(taxonomy => [
        ...(taxonomy.subGenres || []),
        {
          ...taxonomy,
          mainTaxonomy: true,
          subGenres: undefined,
        },
      ]),
  )

  const idBasedTaxonomies = computed(
    () => flatTaxonomies.value.reduce((acc, taxonomy) => ({
      ...acc,
      [taxonomy.genreId]: taxonomy,
    }), {}),
  )

  const subGenreIdsByGenreId = computed(() => {
    return allTaxonomies.value.reduce((acc, taxonomy) => {
      if (taxonomy.subGenres) {
        acc[taxonomy.genreId] = taxonomy.subGenres.map(subGenre => subGenre.genreId)
      }
      return acc
    }, {})
  })

  const getParentTaxonomy = (subGenreId) => {
    const parentId = Object.keys(subGenreIdsByGenreId.value).find(parentId =>
      subGenreIdsByGenreId.value[parentId].includes(subGenreId),
    )
    return parentId ? idBasedTaxonomies.value[parentId] || null : null
  }

  const fetchTaxonomies = async () => {
    try {
      loading.value = true
      const { APP, GAME } = await maRequests.Taxonomy.fetchAllTaxonomies()
      appTaxonomies.value = APP
      gameTaxonomies.value = GAME
    }
    catch (error) {
      maLogger.error('Failed to fetch taxonomies', error)
      maNotification.error({ title: 'Failed to fetch taxonomies' })
    }
    finally {
      loading.value = false
    }
  }

  const getTaxonomyById = id => idBasedTaxonomies.value?.[id] || null

  const selectCategory = (categoryId) => {
    uriStore.replaceRouteParams({
      query: {
        ...route.query,
        [Query.CATEGORY_ID]: categoryId,
        [Query.APP_TAXONOMIES]: [],
        [Query.GAME_TAXONOMIES]: [],
      },
    })
    overlayVisibility.value = false
  }

  const selectTaxonomies = (taxonomies, filterType) => {
    const newTaxKey = filterType === TAXONOMY_TYPES.APP ? Query.APP_TAXONOMIES : Query.GAME_TAXONOMIES
    const removedTaxKey = filterType === TAXONOMY_TYPES.APP ? Query.GAME_TAXONOMIES : Query.APP_TAXONOMIES
    uriStore.replaceRouteParams({
      query: {
        ...route.query,
        [newTaxKey]: compressToEncodedURIComponent(JSON.stringify(taxonomies)),
        [removedTaxKey]: undefined,
        [Query.CATEGORY_ID]: undefined,
      },
    })
    overlayVisibility.value = false
  }

  /**
   * Maps classifications to their respective sub-genres and parent genres.
   *
   * @param {object} classifications - An object where keys are classification types and values are arrays of subGenre IDs.
   * @returns {Array} An array of objects, each containing a sub-genre and its parent genre details.
   */
  const mapClassifications = (classifications = {}) => {
    const allClassificationGenres = Object.values(classifications).flat()
    return allClassificationGenres.reduce((acc, genreId) => {
      const subGenre = getTaxonomyById(genreId)
      if (subGenre) {
        // all classifications are sub genre
        const parent = getParentTaxonomy(genreId)
        acc.push({
          subGenre,
          ...parent,
        })
      }
      return acc
    }, [])
  }

  /**
   * Generates taxonomy table values and metadata for routing.
   *
   * @param {object} data - The data to be classified and mapped.
   * @param {object} routeObj - The route object used for generating redirects.
   * @returns {object} An object containing the main taxonomy and its metadata for routing.
   * @property {string|null} taxonomy - The main taxonomy name and sub-genre name concatenated.
   * @property {object} taxonomyMeta - Metadata for the taxonomies.
   * @property {Array} taxonomyMeta.taxonomies - Array of taxonomies with redirect information.
   * @property {object} taxonomyMeta.redirect - Redirect information for the main taxonomy.
   * @throws Will log an error if mapping taxonomy table values fails.
   */
  const taxonomyTableValues = (data, routeObj = {}) => {
    try {
      const taxonomies = mapClassifications(data)
      const mainTaxonomy = taxonomies?.[0]
      return {
        taxonomy: mainTaxonomy ? `${mainTaxonomy.name} - ${mainTaxonomy.subGenre?.name}` : null,
        taxonomyMeta: {
          taxonomies: taxonomies.map(taxonomy => ({
            ...taxonomy,
            redirect: router.resolve({
              ...routeObj,
              query: {
                ...(routeObj?.query || {}),
                [taxonomy.type === TAXONOMY_TYPES.APP ? 'appTaxonomies' : 'gameTaxonomies']: compressToEncodedURIComponent(JSON.stringify([taxonomy.subGenre?.genreId])),
              },
            }),
          })),
          redirect: router.resolve({
            ...routeObj,
            query: {
              ...(routeObj?.query || {}),
              [mainTaxonomy?.type === TAXONOMY_TYPES.APP ? 'appTaxonomies' : 'gameTaxonomies']: compressToEncodedURIComponent(JSON.stringify([mainTaxonomy?.subGenre?.genreId])),
            },
          }),
        },
      }
    }
    catch (e) {
      maLogger.error('Failed to map taxonomy table values', e)
      return {
        taxonomy: null,
        taxonomyMeta: {
          taxonomies: [],
          redirect: null,
        },
      }
    }
  }

  const $reset = () => {
    appTaxonomies.value = []
    gameTaxonomies.value = []
    loading.value = true
    overlayVisibility.value = false
  }

  return {
    appTaxonomies,
    gameTaxonomies,
    loading,
    overlayVisibility,
    filterType,
    uriTaxonomyIds,
    idBasedTaxonomies,
    subGenreIdsByGenreId,
    fetchTaxonomies,
    selectCategory,
    selectTaxonomies,
    getTaxonomyById,
    getParentTaxonomy,
    taxonomyTableValues,
    $reset,
  }
})
